import { materialAwaiting } from './materialized.js';
import { createTranscript, IMPLICIT_TURN, reduceTranscript } from './model.js';
/** Kinds that never place anything into a turn that is not loaded. */
const NO_PLACEMENT = new Set([
    'progress',
    'run.start',
    'status',
    'ratelimit',
    'usage',
    'turn.end',
    'tool.output',
    'tool.result',
    'question.answered',
    'approval.resolved',
]);
/**
 * Whether `events` (seq-ordered, starting at the cut `[seq, turn]`, one of `m.cuts`) reduce to
 * exactly the full transcript's entries from that turn on. Cuts already exclude open asks,
 * queued messages and running subagents/background tasks; this rejects what the events show:
 * an earlier turn's id on anything placed, or a reference to an item the window cannot see.
 */
export function windowSafe(events, m, cut) {
    const [seq, turn] = cut;
    if (turn <= 1)
        return true;
    const first = events[0];
    const turnId = m.turnIds[turn - 1];
    if (!first || first.seq !== seq || !turnId || (first.turnId ?? IMPLICIT_TURN) !== turnId)
        return false;
    const prior = new Set(m.turnIds.slice(0, turn - 1));
    const intro = new Set();
    for (const e of events) {
        const placesNothing = NO_PLACEMENT.has(e.kind) ||
            (e.kind === 'marker' && e.marker === 'title') ||
            (e.kind === 'user.msg' && e.queued && !!e.messageId);
        if (e.turnId && prior.has(e.turnId) && !placesNothing)
            return false;
        if (e.parentId && !intro.has(e.parentId) && !placesNothing)
            return false;
        switch (e.kind) {
            case 'tool.call':
                intro.add(e.toolCallId);
                break;
            case 'cli.task':
                if (e.toolCallId && !intro.has(e.toolCallId))
                    return false;
                break;
            case 'artifact':
                if ((m.artifacts[e.artifactId] ?? turn) < turn && !intro.has(`artifact:${e.artifactId}`))
                    return false;
                intro.add(`artifact:${e.artifactId}`);
                break;
        }
    }
    return true;
}
/** Reduce a window's events; turn indexes continue after the `turnBase` earlier turns. */
export function buildWindow(events, turnBase, runId = '') {
    let v = { ...createTranscript(runId), turnBase };
    for (const e of events)
        v = reduceTranscript(v, e);
    return v;
}
/** The window's turns under the run's materialized header (exact run-level fields). */
export function composeWindow(win, m) {
    return {
        ...win,
        ...m.header,
        awaiting: materialAwaiting(m),
        entries: win.entries,
        refs: win.refs,
        connection: win.connection,
        turnBase: win.turnBase ?? 0,
        material: m,
    };
}
/**
 * The newest cut that still shows at least `turns` turns (and starts at or before `atSeq`, for
 * deep links). `undefined` = load from the beginning.
 */
export function pickCut(m, o) {
    const total = m.turnIds.length;
    for (let i = m.cuts.length - 1; i >= 0; i--) {
        const c = m.cuts[i];
        if (!c || c[1] <= 1)
            return undefined;
        if (total - c[1] + 1 < o.turns)
            continue;
        if (o.atSeq != null && c[0] > o.atSeq)
            continue;
        if (o.before != null && c[0] >= o.before)
            continue;
        return c;
    }
    return undefined;
}
//# sourceMappingURL=window.js.map